import { useState, useMemo, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Search,
  Users,
  UserCog,
  Building2,
  ShieldCheck,
  Network,
  Crown,
  Code,
  DollarSign,
  TrendingUp,
  Sparkles,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  ChevronsDownUp,
  ChevronsUpDown,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Modal } from '@/shared/components/ui/Modal';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { StatTile } from './StatTile';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrgNode {
  id: string;
  display_name: string;
  email?: string;
  role: string;
  department: string;
  position: string;
  manager_id: string | null;
  team_leader_of: string;
  children: OrgNode[];
}

export interface OrgChartResponse {
  roots: OrgNode[];
  nodeCount: number;
  /** Whether the viewer may change reporting lines (hr.organization.manage). */
  canManage?: boolean;
}

type ViewMode = 'hierarchy' | 'department';

// ─── Department Meta & Styling ────────────────────────────────────────────────

export interface DepartmentConfig {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: React.ReactNode;
}

const DEPARTMENT_CONFIG: Record<string, DepartmentConfig> = {
  general: {
    label: 'General',
    color: '#64748b',
    bgColor: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
    borderColor: 'border-slate-500/30',
    icon: <Building2 className="h-2.5 w-2.5" />,
  },
  development: {
    label: 'Development',
    color: '#4f46e5',
    bgColor: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    borderColor: 'border-indigo-500/30',
    icon: <Code className="h-2.5 w-2.5" />,
  },
  sales: {
    label: 'Sales',
    color: '#e11d48',
    bgColor: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
    borderColor: 'border-rose-500/30',
    icon: <TrendingUp className="h-2.5 w-2.5" />,
  },
  hr: {
    label: 'Human Resources',
    color: '#d97706',
    bgColor: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    borderColor: 'border-amber-500/30',
    icon: <Users className="h-2.5 w-2.5" />,
  },
  accounts: {
    label: 'Accounts & Finance',
    color: '#059669',
    bgColor: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    borderColor: 'border-emerald-500/30',
    icon: <DollarSign className="h-2.5 w-2.5" />,
  },
  creative: {
    label: 'Creative & Marketing',
    color: '#9333ea',
    bgColor: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    borderColor: 'border-purple-500/30',
    icon: <Sparkles className="h-2.5 w-2.5" />,
  },
  superadmin: {
    label: 'Management',
    color: '#7c3aed',
    bgColor: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    borderColor: 'border-violet-500/30',
    icon: <Crown className="h-2.5 w-2.5" />,
  },
  it: {
    label: 'IT & Systems',
    color: '#0284c7',
    bgColor: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    borderColor: 'border-sky-500/30',
    icon: <Network className="h-2.5 w-2.5" />,
  },
};

export function normalizeDept(dept: string): string {
  const d = String(dept ?? '').trim().toLowerCase();
  if (!d || d === 'other' || d === 'general' || d === 'none') return 'general';
  if (d.includes('dev') || d.includes('software') || d.includes('eng')) return 'development';
  if (d.includes('sale') || d.includes('bde')) return 'sales';
  if (d.includes('hr') || d.includes('human') || d.includes('people')) return 'hr';
  if (d.includes('account') || d.includes('finance')) return 'accounts';
  if (d.includes('creative') || d.includes('design') || d.includes('market')) return 'creative';
  if (
    d.includes('super') ||
    d.includes('manage') ||
    d.includes('exec') ||
    ((d.includes('admin') || d.includes('owner')) && !d.includes('dept'))
  ) {
    return 'superadmin';
  }
  return 'general';
}

export function getDeptConfig(dept: string): DepartmentConfig {
  const key = normalizeDept(dept);
  return (
    DEPARTMENT_CONFIG[key] ?? {
      label: 'General',
      color: '#64748b',
      bgColor: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
      borderColor: 'border-slate-500/30',
      icon: <Building2 className="h-2.5 w-2.5" />,
    }
  );
}

// ─── Role Labels & Colors ─────────────────────────────────────────────────────

export const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  team_lead: 'Team Lead',
  department_admin: 'Dept Admin',
  bde: 'BDE',
  developer: 'Developer',
  employee: 'Employee',
};

export const ROLE_COLORS: Record<string, string> = {
  superadmin: '#7c3aed',
  admin: '#2563eb',
  manager: '#0891b2',
  team_lead: '#059669',
  department_admin: '#d97706',
  bde: '#dc2626',
  developer: '#4f46e5',
  employee: '#6b7280',
};

export const ROLE_RANK: Record<string, number> = {
  superadmin: 1,
  owner: 1,
  admin: 2,
  department_admin: 2,
  manager: 3,
  team_lead: 4,
  bde: 5,
  developer: 5,
  employee: 6,
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useOrgChart() {
  return useQuery({
    queryKey: ['org-chart'],
    queryFn: async () => {
      const { data } = await api.get<OrgChartResponse>('/api/org-chart');
      return data;
    },
  });
}

function useReassignManager() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, managerId }: { userId: string; managerId: string | null }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/users/${userId}/manager`, {
        manager_id: managerId,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-chart'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function flattenTree(nodes: OrgNode[]): OrgNode[] {
  const result: OrgNode[] = [];
  function walk(node: OrgNode) {
    result.push(node);
    node.children.forEach(walk);
  }
  nodes.forEach(walk);
  return result;
}

export function cloneNode(node: OrgNode): OrgNode {
  return {
    ...node,
    children: node.children ? node.children.map(cloneNode) : [],
  };
}

export function isUzefDurrani(node: { email?: string; display_name?: string; position?: string; role?: string }): boolean {
  const email = (node.email || '').trim().toLowerCase();
  if (email === 'uzef@durranitech.com') return true;
  const name = (node.display_name || '').trim().toLowerCase();
  return name.includes('uzef') && name.includes('durrani');
}

export function isActualDeptAdmin(node: { role?: string; position?: string; email?: string }): boolean {
  if (isUzefDurrani(node)) return false;
  const role = (node.role || '').toLowerCase();
  const position = (node.position || '').toLowerCase();
  return (
    role === 'admin' ||
    role === 'department_admin' ||
    position.includes('administrator') ||
    (position.includes('admin') && !position.includes('super')) ||
    position.includes('head of') ||
    position.includes('department head')
  );
}

export function isActualLead(node: { role?: string; position?: string; email?: string }): boolean {
  if (isUzefDurrani(node) || isActualDeptAdmin(node)) return false;
  const role = (node.role || '').toLowerCase();
  const position = (node.position || '').toLowerCase();
  return (
    role === 'manager' ||
    role === 'team_lead' ||
    position.includes('manager') ||
    position.includes('team lead') ||
    (position.includes('lead') && !position.includes('leadership')) ||
    position.includes('director')
  );
}

function nodeMatchesSearch(node: OrgNode, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    node.display_name.toLowerCase().includes(q) ||
    (node.email || '').toLowerCase().includes(q) ||
    (node.department || '').toLowerCase().includes(q) ||
    (node.position || '').toLowerCase().includes(q) ||
    (ROLE_LABELS[node.role] ?? node.role ?? '').toLowerCase().includes(q)
  );
}

function treeContainsMatch(node: OrgNode, query: string): boolean {
  if (nodeMatchesSearch(node, query)) return true;
  return node.children.some((child) => treeContainsMatch(child, query));
}

/**
 * Builds an authentic Organization Hierarchy structured strictly by:
 * Level 1: Super Admin / Executive Leadership (Uzef Durrani exclusively)
 * Level 2: Department Administrators (Shaikh Shoaib, Sandesh Kajale, Siddhi Chuttar, etc.)
 * Level 3: Department Functional Roles & Team Members (Developers, Sales Reps, Marketing Specialists)
 *
 * Departments with NO administrator specified do NOT have random employees promoted to admin.
 * Their functional roles connect directly under Executive Leadership.
 */
export function buildDepartmentHierarchy(allUsers: OrgNode[]): OrgNode[] {
  if (allUsers.length === 0) return [];

  const userMap = new Map<string, OrgNode>();
  allUsers.forEach((u) => {
    userMap.set(u.id, {
      ...u,
      children: [],
    });
  });

  // 1. Identify primary leadership: Super Admin (Uzef Durrani exclusively)
  const uzef = allUsers.find(isUzefDurrani);
  const rootUser =
    uzef ||
    allUsers.find((u) => {
      const r = (u.role || '').toLowerCase();
      const pos = (u.position || '').toLowerCase();
      return r === 'superadmin' || r === 'owner' || pos.includes('super admin') || pos.includes('owner');
    }) ||
    allUsers[0];

  const rootNode = userMap.get(rootUser.id)!;

  // 2. Group all other users by normalized department
  const deptMap = new Map<string, OrgNode[]>();
  allUsers.forEach((u) => {
    if (u.id === rootUser.id) return; // Root handled separately
    const deptKey = normalizeDept(u.department);
    if (!deptMap.has(deptKey)) deptMap.set(deptKey, []);
    deptMap.get(deptKey)!.push(u);
  });

  // 3. Connect hierarchy: Department Admins lead their department members; unassigned departments report as functional roles
  deptMap.forEach((members) => {
    // Check if an actual Department Administrator or Lead is specified
    const deptAdmin = members.find((m) => isActualDeptAdmin(m));
    const deptLead = !deptAdmin ? members.find((m) => isActualLead(m)) : undefined;
    const designatedHead = deptAdmin || deptLead;

    if (designatedHead) {
      // Department HAS an administrator or lead
      const headNode = userMap.get(designatedHead.id)!;
      const subordinates = members.filter((m) => m.id !== designatedHead.id);

      subordinates.forEach((member) => {
        const memberNode = userMap.get(member.id)!;

        // Check if member has an explicit manager within this department (who is not root)
        const explicitManagerInDept =
          member.manager_id &&
          member.manager_id !== member.id &&
          member.manager_id !== rootUser.id &&
          members.some((m) => m.id === member.manager_id);

        if (explicitManagerInDept) {
          const mgrNode = userMap.get(member.manager_id!)!;
          if (!mgrNode.children.some((c) => c.id === memberNode.id)) {
            mgrNode.children.push(memberNode);
          }
        } else {
          // Employee reports to the designated Department Administrator
          if (!headNode.children.some((c) => c.id === memberNode.id)) {
            headNode.children.push(memberNode);
          }
        }
      });

      // Attach Department Administrator directly under Executive Leadership
      if (!rootNode.children.some((c) => c.id === headNode.id)) {
        rootNode.children.push(headNode);
      }
    } else {
      // Department has NO administrator specified (e.g. Sales, Creative)
      // Do NOT arbitrarily promote any employee to admin or make colleagues report to each other!
      members.forEach((member) => {
        const memberNode = userMap.get(member.id)!;

        const explicitMgr =
          member.manager_id &&
          member.manager_id !== member.id &&
          userMap.has(member.manager_id);

        if (explicitMgr && member.manager_id !== rootUser.id) {
          const mgrNode = userMap.get(member.manager_id!)!;
          if (!mgrNode.children.some((c) => c.id === memberNode.id)) {
            mgrNode.children.push(memberNode);
          }
        } else {
          // Reports directly to Executive Leadership as functional role
          if (!rootNode.children.some((c) => c.id === memberNode.id)) {
            rootNode.children.push(memberNode);
          }
        }
      });
    }
  });

  // Sort Level 2: Department Admins first, then Leads, then by department and display name
  rootNode.children.sort((a, b) => {
    const isAAdmin = isActualDeptAdmin(a);
    const isBAdmin = isActualDeptAdmin(b);
    if (isAAdmin && !isBAdmin) return -1;
    if (!isAAdmin && isBAdmin) return 1;

    const isALead = isActualLead(a);
    const isBLead = isActualLead(b);
    if (isALead && !isBLead) return -1;
    if (!isALead && isBLead) return 1;

    if (a.department !== b.department) {
      return a.department.localeCompare(b.department);
    }
    return a.display_name.localeCompare(b.display_name);
  });

  return [rootNode];
}

/**
 * Filters the hierarchy tree for a selected department while preserving
 * Executive Leadership at the top and showing all functional roles & sub-branches
 * that belong to the department.
 */
export function filterHierarchyTree(roots: OrgNode[], deptKey: string): OrgNode[] {
  if (deptKey === 'all') return roots;

  function filterNode(node: OrgNode, isTopExecutive: boolean): OrgNode | null {
    const nodeDept = normalizeDept(node.department);
    const isDeptMatch = nodeDept === deptKey;

    // Filter children recursively
    const matchingChildren: OrgNode[] = [];
    for (const child of node.children || []) {
      const filteredChild = filterNode(child, false);
      if (filteredChild) {
        matchingChildren.push(filteredChild);
      }
    }

    // Top Executive (Uzef Durrani) is the root anchor of the org chart
    if (isTopExecutive) {
      return {
        ...node,
        children: matchingChildren,
      };
    }

    // If this node matches the department, keep it and all matching children
    if (isDeptMatch) {
      return {
        ...node,
        children:
          matchingChildren.length > 0
            ? matchingChildren
            : (node.children || []).filter((c) => normalizeDept(c.department) === deptKey).map(cloneNode),
      };
    }

    // If node itself isn't in deptKey, but has descendants in deptKey
    if (matchingChildren.length > 0) {
      return {
        ...node,
        children: matchingChildren,
      };
    }

    return null;
  }

  const result: OrgNode[] = [];
  for (const root of roots) {
    const filtered = filterNode(root, isUzefDurrani(root));
    if (filtered) {
      result.push(filtered);
    }
  }
  return result;
}

// ─── Main OrgChart Component ──────────────────────────────────────────────────

export function OrgChart() {
  const { data, isLoading, isError } = useOrgChart();
  const isAdmin = data?.canManage === true;
  const [search, setSearch] = useState('');
  const [selectedDept, setSelectedDept] = useState<string>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('hierarchy');
  const [zoomLevel, setZoomLevel] = useState<number>(100);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [reassignTarget, setReassignTarget] = useState<OrgNode | null>(null);

  const toggleCollapse = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allUsers = useMemo(() => {
    if (!data) return [];
    return flattenTree(data.roots);
  }, [data]);

  // Unique list of departments with member counts
  const departmentStats = useMemo(() => {
    const map = new Map<string, { key: string; label: string; count: number }>();
    allUsers.forEach((u) => {
      const key = normalizeDept(u.department);
      const conf = getDeptConfig(u.department);
      if (!map.has(key)) {
        map.set(key, { key, label: conf.label, count: 0 });
      }
      map.get(key)!.count += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [allUsers]);

  // Derived org summary stats
  const orgStats = useMemo(() => {
    const departments = new Set<string>();
    let managers = 0;
    let leadership = 0;
    for (const u of allUsers) {
      if (u.department && normalizeDept(u.department) !== 'superadmin') {
        departments.add(normalizeDept(u.department));
      }
      if (u.children.length > 0) managers++;
      if (isUzefDurrani(u) || isActualDeptAdmin(u)) {
        leadership++;
      }
    }
    return { departments: departments.size, managers, leadership };
  }, [allUsers]);

  // Construct hierarchy tree
  const hierarchyRoots = useMemo(() => {
    if (!data) return [];
    return buildDepartmentHierarchy(allUsers);
  }, [data, allUsers]);

  // Filter roots by department if a filter is active
  const filteredRoots = useMemo(() => {
    return filterHierarchyTree(hierarchyRoots, selectedDept);
  }, [hierarchyRoots, selectedDept]);

  // Expand / Collapse all handlers
  const handleExpandAll = () => setCollapsed(new Set());
  const handleCollapseAll = () => {
    const allIds = new Set<string>();
    function collect(nodes: OrgNode[]) {
      for (const n of nodes) {
        if (n.children && n.children.length > 0) {
          allIds.add(n.id);
          collect(n.children);
        }
      }
    }
    collect(filteredRoots);
    setCollapsed(allIds);
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isError || !data) {
    return <EmptyState title="Failed to load org chart" description="Check your connection and try again." />;
  }

  if (data.roots.length === 0) {
    return <EmptyState title="No employees found" description="Add users to see the organization chart." />;
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden bg-surface-container-lowest/50">
      {/* Header Bar */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm ring-1 ring-primary/20">
            <Network className="h-6 w-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-display-sm font-bold text-on-surface tracking-tight">Organization Chart</h1>
              <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                {data.nodeCount} Team Members
              </span>
            </div>
            <p className="text-body-sm text-on-surface-variant">
              Hierarchy structured by Executive Leadership, Department Administrators, and Functional Roles
            </p>
          </div>
        </div>

        {/* View Switcher, Search & Controls */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* View Mode Switcher */}
          <div className="flex items-center rounded-lg border border-outline bg-surface p-0.5 shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode('hierarchy')}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                viewMode === 'hierarchy'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-variant/40'
              }`}
              title="Organized by Department and Admin Hierarchy"
            >
              <Network className="h-3.5 w-3.5" />
              <span>Hierarchy Tree</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('department')}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                viewMode === 'department'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-variant/40'
              }`}
              title="Matrix view grouped by Departments"
            >
              <Building2 className="h-3.5 w-3.5" />
              <span>By Department</span>
            </button>
          </div>

          {/* Search Input */}
          <div className="w-[230px]">
            <Input
              name="org-search"
              placeholder="Search employee, admin, role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="h-4 w-4 text-on-surface-variant" />}
            />
          </div>
        </div>
      </div>

      {/* Summary KPI Tiles */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile icon={Users} label="Total Workforce" value={data.nodeCount} tone="primary" index={0} />
        <StatTile icon={Building2} label="Departments" value={orgStats.departments} tone="info" index={1} />
        <StatTile icon={ShieldCheck} label="Admins & Leadership" value={orgStats.leadership} tone="warning" index={2} />
        <StatTile icon={Network} label="Reporting Managers" value={orgStats.managers} tone="success" index={3} />
      </div>

      {/* Department Filter Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-outline/50 pb-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs font-medium text-on-surface-variant">Departments:</span>
          <button
            type="button"
            onClick={() => setSelectedDept('all')}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              selectedDept === 'all'
                ? 'bg-primary text-white shadow-sm'
                : 'border border-outline bg-surface text-on-surface-variant hover:bg-surface-variant/60'
            }`}
          >
            All ({allUsers.length})
          </button>
          {departmentStats.map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => setSelectedDept(d.key)}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                selectedDept === d.key
                  ? 'bg-primary text-white shadow-sm'
                  : 'border border-outline bg-surface text-on-surface-variant hover:bg-surface-variant/60'
              }`}
            >
              <span>{d.label}</span>
              <span
                className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                  selectedDept === d.key ? 'bg-white/20 text-white' : 'bg-surface-container-high text-on-surface'
                }`}
              >
                {d.count}
              </span>
            </button>
          ))}
        </div>

        {/* Tree Canvas Zoom & Expand Controls */}
        {viewMode !== 'department' && (
          <div className="flex items-center gap-1 text-xs text-on-surface-variant">
            <button
              type="button"
              onClick={() => setZoomLevel((z) => Math.max(50, z - 15))}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-outline bg-surface hover:bg-surface-variant"
              title="Zoom Out"
            >
              <ZoomOut className="h-3.5 w-3.5" />
            </button>
            <span className="w-12 text-center text-[11px] font-semibold">{zoomLevel}%</span>
            <button
              type="button"
              onClick={() => setZoomLevel((z) => Math.min(150, z + 15))}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-outline bg-surface hover:bg-surface-variant"
              title="Zoom In"
            >
              <ZoomIn className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setZoomLevel(100)}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-outline bg-surface hover:bg-surface-variant"
              title="Reset Zoom"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            <div className="mx-1 h-4 w-px bg-outline" />
            <button
              type="button"
              onClick={handleExpandAll}
              className="flex items-center gap-1 rounded-md border border-outline bg-surface px-2 py-1 hover:bg-surface-variant text-[11px]"
              title="Expand All"
            >
              <ChevronsUpDown className="h-3.5 w-3.5" />
              <span>Expand</span>
            </button>
            <button
              type="button"
              onClick={handleCollapseAll}
              className="flex items-center gap-1 rounded-md border border-outline bg-surface px-2 py-1 hover:bg-surface-variant text-[11px]"
              title="Collapse All"
            >
              <ChevronsDownUp className="h-3.5 w-3.5" />
              <span>Collapse</span>
            </button>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="relative flex-1 overflow-auto rounded-xl border border-outline bg-surface p-6 shadow-inner">
        {viewMode === 'department' ? (
          /* ─── Department Cards Matrix View ─── */
          <DepartmentMatrixView
            allUsers={allUsers}
            selectedDept={selectedDept}
            search={search}
            isAdmin={isAdmin}
            onReassign={setReassignTarget}
          />
        ) : (
          /* ─── Hierarchy Tree Canvas ─── */
          <div
            className="flex min-w-max flex-col items-center justify-start pb-12 transition-transform duration-200"
            style={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top center' }}
          >
            {filteredRoots.length === 0 ? (
              <div className="py-12">
                <EmptyState
                  title="No team members in this department"
                  description="Try selecting 'All' or a different department."
                />
              </div>
            ) : (
              filteredRoots.map((root, i) => (
                <div key={root.id} className={i > 0 ? 'mt-12' : ''}>
                  <TreeNode
                    node={root}
                    search={search}
                    collapsed={collapsed}
                    onToggle={toggleCollapse}
                    isAdmin={isAdmin}
                    onReassign={setReassignTarget}
                    isRoot={true}
                    level={0}
                  />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Reassign Reporting Manager Modal */}
      {reassignTarget && (
        <ReassignModal
          target={reassignTarget}
          allUsers={allUsers}
          onClose={() => setReassignTarget(null)}
        />
      )}
    </div>
  );
}

// ─── TreeNode Component (Recursive Tree) ──────────────────────────────────────

function TreeNode({
  node,
  search,
  collapsed,
  onToggle,
  isAdmin,
  onReassign,
  isRoot = false,
  level = 0,
}: {
  node: OrgNode;
  search: string;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  isAdmin: boolean;
  onReassign: (node: OrgNode) => void;
  isRoot?: boolean;
  level?: number;
}) {
  const hasChildren = node.children && node.children.length > 0;
  const isCollapsed = collapsed.has(node.id);
  const isMatch = search ? nodeMatchesSearch(node, search) : false;
  const subtreeMatch = search ? treeContainsMatch(node, search) : true;

  if (search && !subtreeMatch) return null;

  const visibleChildren = search
    ? node.children.filter((c) => treeContainsMatch(c, search))
    : node.children;

  const deptConf = getDeptConfig(node.department);
  const DeptIcon = deptConf.icon;

  const isSuperAdmin = isUzefDurrani(node);
  const isDeptAdmin = isActualDeptAdmin(node);
  const isLead = isActualLead(node);

  return (
    <div className="flex flex-col items-center">
      {/* Node Card */}
      <div
        className={`group relative rounded-xl border bg-surface transition-all duration-200 ${
          isMatch
            ? 'ring-2 ring-primary ring-offset-2 border-primary shadow-lg'
            : isSuperAdmin
            ? 'border-violet-400 dark:border-violet-600 shadow-md hover:shadow-lg'
            : isDeptAdmin
            ? 'border-indigo-400 dark:border-indigo-600 shadow-sm hover:shadow-md'
            : isLead
            ? 'border-teal-400 dark:border-teal-600 shadow-sm hover:shadow-md'
            : 'border-outline hover:border-outline-variant shadow-sm hover:shadow'
        }`}
        style={{ width: '220px' }}
      >
        {/* Tier Header Strip */}
        <div
          className={`flex items-center justify-between rounded-t-xl px-3 py-1 text-[10px] font-semibold tracking-wide ${
            isSuperAdmin
              ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white'
              : isDeptAdmin
              ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
              : isLead
              ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white'
              : 'bg-surface-container-high text-on-surface-variant'
          }`}
        >
          <div className="flex items-center gap-1.5 truncate">
            {isSuperAdmin ? (
              <>
                <Crown className="h-3 w-3 text-amber-300" />
                <span>Executive Leadership</span>
              </>
            ) : isDeptAdmin ? (
              <>
                <ShieldCheck className="h-3 w-3 text-blue-200" />
                <span>Department Administrator</span>
              </>
            ) : isLead ? (
              <>
                <Network className="h-3 w-3 text-teal-200" />
                <span>Team Lead / Manager</span>
              </>
            ) : (
              <span>Functional Role</span>
            )}
          </div>

          {/* Direct reports count pill */}
          {hasChildren && (
            <span
              className={`rounded-full px-1.5 py-0.2 text-[9px] font-bold ${
                isSuperAdmin || isDeptAdmin || isLead ? 'bg-white/20 text-white' : 'bg-surface text-on-surface'
              }`}
            >
              {node.children.length}
            </span>
          )}
        </div>

        {/* Card Body */}
        <div className="p-3.5">
          {/* Avatar & Department Icon Badge */}
          <div className="relative mb-2 flex items-center justify-center">
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-full text-base font-bold text-white shadow-sm ring-2 ${
                isSuperAdmin
                  ? 'bg-gradient-to-br from-violet-600 to-indigo-700 ring-violet-300'
                  : isDeptAdmin
                  ? 'bg-gradient-to-br from-indigo-600 to-blue-600 ring-indigo-300'
                  : isLead
                  ? 'bg-gradient-to-br from-teal-600 to-emerald-600 ring-teal-300'
                  : 'bg-gradient-to-br from-slate-600 to-slate-800 ring-slate-200'
              }`}
            >
              {node.display_name
                .split(' ')
                .map((w) => w[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()}
            </div>

            {/* Department icon badge attached to avatar */}
            <div
              className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-surface shadow-xs"
              style={{ color: deptConf.color }}
              title={deptConf.label}
            >
              {deptConf.icon}
            </div>
          </div>

          {/* Name */}
          <h3 className="text-center text-sm font-bold text-on-surface leading-tight truncate" title={node.display_name}>
            {node.display_name}
          </h3>

          {/* Position or Role */}
          <p className="mt-0.5 text-center text-[11px] text-on-surface-variant truncate" title={node.position}>
            {isSuperAdmin ? 'Super Admin' : (node.position || ROLE_LABELS[node.role] || node.role)}
          </p>

          {/* Email badge for superadmin / available */}
          {(isSuperAdmin || node.email) && (
            <p className="text-center text-[10px] text-on-surface-variant/80 truncate font-mono mt-0.5" title={node.email || 'uzef@durranitech.com'}>
              {node.email || (isSuperAdmin ? 'uzef@durranitech.com' : '')}
            </p>
          )}

          {/* Department Badge */}
          <div className="mt-2 flex justify-center">
            <span
              className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-medium ${deptConf.bgColor} ${deptConf.borderColor}`}
            >
              {deptConf.icon}
              <span>{deptConf.label}</span>
            </span>
          </div>

          {/* Card Footer: Expand/Collapse & Reassign Manager Actions */}
          <div className="mt-2.5 flex items-center justify-center gap-1.5 border-t border-outline/40 pt-2">
            {hasChildren && (
              <button
                type="button"
                onClick={() => onToggle(node.id)}
                className="flex items-center gap-1 rounded-full border border-outline bg-surface px-2 py-0.5 text-[10px] font-medium text-on-surface-variant hover:bg-surface-variant"
                aria-label={isCollapsed ? 'Expand Reports' : 'Collapse Reports'}
                title={isCollapsed ? 'Show Direct Reports' : 'Hide Direct Reports'}
              >
                {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                <span>
                  {node.children.length} {node.children.length === 1 ? 'Report' : 'Reports'}
                </span>
              </button>
            )}

            {isAdmin && (
              <button
                type="button"
                onClick={() => onReassign(node)}
                className="flex h-6 w-6 items-center justify-center rounded-full border border-outline bg-surface text-on-surface-variant hover:bg-primary/10 hover:text-primary hover:border-primary/40 transition-colors"
                aria-label="Change manager"
                title="Change Reporting Manager"
              >
                <UserCog className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Children Hierarchy Connector Lines */}
      {hasChildren && !isCollapsed && visibleChildren.length > 0 && (
        <div className="flex flex-col items-center">
          {/* Vertical trunk line from parent */}
          <div className="h-6 w-0.5 bg-outline-variant" />

          {visibleChildren.length === 1 ? (
            <TreeNode
              node={visibleChildren[0]}
              search={search}
              collapsed={collapsed}
              onToggle={onToggle}
              isAdmin={isAdmin}
              onReassign={onReassign}
              level={level + 1}
            />
          ) : (
            <div className="relative flex items-start justify-center pt-3">
              {/* Horizontal branch bar linking all children */}
              <div
                className="absolute top-0 h-0.5 bg-outline-variant"
                style={{
                  left: 'calc(110px)',
                  right: 'calc(110px)',
                }}
              />

              {/* Children Nodes Grid */}
              <div className="flex gap-6">
                {visibleChildren.map((child) => (
                  <div key={child.id} className="relative flex flex-col items-center">
                    {/* Vertical drop line connecting branch bar to child */}
                    <div className="absolute -top-3 h-3 w-0.5 bg-outline-variant" />
                    <TreeNode
                      node={child}
                      search={search}
                      collapsed={collapsed}
                      onToggle={onToggle}
                      isAdmin={isAdmin}
                      onReassign={onReassign}
                      level={level + 1}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Department Matrix View ───────────────────────────────────────────────────

function DepartmentMatrixView({
  allUsers,
  selectedDept,
  search,
  isAdmin,
  onReassign,
}: {
  allUsers: OrgNode[];
  selectedDept: string;
  search: string;
  isAdmin: boolean;
  onReassign: (node: OrgNode) => void;
}) {
  const executive = useMemo(() => allUsers.find(isUzefDurrani), [allUsers]);

  // Group users by department (excluding Uzef who is top-level Executive Leadership)
  const groupedDepartments = useMemo(() => {
    const map = new Map<string, OrgNode[]>();
    allUsers.forEach((u) => {
      if (isUzefDurrani(u)) return;
      if (search && !nodeMatchesSearch(u, search)) return;
      const key = normalizeDept(u.department);
      if (selectedDept !== 'all' && key !== selectedDept) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(u);
    });

    const list = Array.from(map.entries()).map(([deptKey, members]) => {
      // Find actual specified Department Administrator or Lead
      const admin = members.find((m) => isActualDeptAdmin(m));
      const lead = !admin ? members.find((m) => isActualLead(m)) : undefined;
      const head = admin || lead || null;

      // If there is an admin/lead, the rest of members are team. If no admin/lead, ALL members are in team!
      const team = head ? members.filter((m) => m.id !== head.id) : [...members];

      team.sort((a, b) => {
        const rankA = ROLE_RANK[a.role.toLowerCase()] ?? 6;
        const rankB = ROLE_RANK[b.role.toLowerCase()] ?? 6;
        if (rankA !== rankB) return rankA - rankB;
        return a.display_name.localeCompare(b.display_name);
      });

      const conf = getDeptConfig(deptKey);

      return {
        key: deptKey,
        config: conf,
        head,
        team,
        total: members.length,
      };
    });

    list.sort((a, b) => b.total - a.total);
    return list;
  }, [allUsers, selectedDept, search]);

  return (
    <div className="space-y-6">
      {/* Executive Leadership Spotlight (Uzef Durrani exclusively) */}
      {executive && (selectedDept === 'all' || selectedDept === 'superadmin') && (
        <div className="rounded-xl border-2 border-violet-400/50 bg-gradient-to-r from-violet-600/10 via-indigo-600/5 to-purple-600/10 p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-indigo-700 text-lg font-bold text-white shadow-md ring-2 ring-violet-300">
                <Crown className="h-6 w-6 text-amber-300" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-on-surface">{executive.display_name}</h3>
                  <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 px-2.5 py-0.5 text-[10px] font-bold text-white shadow-xs">
                    <Crown className="h-3 w-3 text-amber-300" />
                    Executive Leadership
                  </span>
                </div>
                <p className="text-xs text-on-surface-variant font-medium">
                  {executive.position || 'Super Admin'} &bull; <span className="font-mono">{executive.email || 'uzef@durranitech.com'}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-lg bg-surface px-3 py-1.5 text-xs font-semibold text-on-surface border border-outline/60 shadow-xs">
                Organization Super Administrator
              </span>
            </div>
          </div>
        </div>
      )}

      {groupedDepartments.length === 0 ? (
        <EmptyState title="No matching departments" description="Try selecting 'All Departments' or clear search." />
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {groupedDepartments.map((dept) => {
            const DeptIcon = dept.config.icon;
            return (
              <div
                key={dept.key}
                className="flex flex-col rounded-xl border border-outline bg-surface-container-low/40 p-4 shadow-sm"
              >
                {/* Department Card Header */}
                <div className="mb-3 flex items-center justify-between border-b border-outline/40 pb-3">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-lg shadow-xs"
                      style={{ backgroundColor: `${dept.config.color}20`, color: dept.config.color }}
                    >
                      {dept.config.icon}
                    </div>
                    <div>
                      <h2 className="text-sm font-bold text-on-surface">{dept.config.label}</h2>
                      <p className="text-[11px] text-on-surface-variant">{dept.total} Team Members</p>
                    </div>
                  </div>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${dept.config.bgColor} border ${dept.config.borderColor}`}
                  >
                    {dept.total}
                  </span>
                </div>

                {/* Department Administrator / Head Section */}
                <div className="mb-4">
                  <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold text-primary">
                    <div className="flex items-center gap-1">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      <span>Department Administrator</span>
                    </div>
                    {dept.head && (
                      <span className="text-[10px] text-on-surface-variant font-normal">
                        Assigned
                      </span>
                    )}
                  </div>

                  {dept.head ? (
                    <div className="relative rounded-lg border-2 border-indigo-400/40 bg-surface p-3 shadow-xs">
                      <div className="flex items-center gap-3">
                        <div
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-xs"
                          style={{ backgroundColor: dept.config.color }}
                        >
                          {dept.head.display_name
                            .split(' ')
                            .map((w) => w[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-1">
                            <h4 className="truncate text-xs font-bold text-on-surface">{dept.head.display_name}</h4>
                            <span className="shrink-0 rounded bg-indigo-500/10 px-1.5 py-0.2 text-[9px] font-bold text-indigo-600">
                              {ROLE_LABELS[dept.head.role] ?? 'Admin'}
                            </span>
                          </div>
                          <p className="truncate text-[10px] text-on-surface-variant">
                            {dept.head.position || dept.head.department}
                          </p>
                        </div>
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => onReassign(dept.head!)}
                            className="flex h-7 w-7 items-center justify-center rounded-md border border-outline hover:bg-surface-variant text-on-surface-variant"
                            title="Change Manager"
                          >
                            <UserCog className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-outline/70 bg-surface/40 p-3 text-center">
                      <p className="text-xs font-medium text-on-surface-variant">No Administrator Assigned</p>
                      <p className="text-[10px] text-on-surface-variant/70 mt-0.5">
                        Admin is specified when creating a user for this department
                      </p>
                    </div>
                  )}
                </div>

                {/* Department Team Members List */}
                <div className="flex-1">
                  <p className="mb-2 text-[11px] font-semibold text-on-surface-variant">
                    Team Members & Roles ({dept.team.length})
                  </p>
                  {dept.team.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-outline p-3 text-center text-xs text-on-surface-variant">
                      No other members in this department
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                      {dept.team.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between rounded-lg border border-outline/70 bg-surface p-2.5 hover:border-outline transition-colors"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div
                              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                              style={{ backgroundColor: ROLE_COLORS[member.role] ?? '#6b7280' }}
                            >
                              {member.display_name
                                .split(' ')
                                .map((w) => w[0])
                                .join('')
                                .slice(0, 2)
                                .toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-xs font-semibold text-on-surface">{member.display_name}</p>
                              <p className="truncate text-[10px] text-on-surface-variant">
                                {member.position || ROLE_LABELS[member.role] || member.role}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="rounded bg-surface-container-high px-1.5 py-0.5 text-[9px] font-medium text-on-surface-variant">
                              {ROLE_LABELS[member.role] ?? member.role}
                            </span>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => onReassign(member)}
                                className="flex h-6 w-6 items-center justify-center rounded hover:bg-surface-variant text-on-surface-variant"
                                title="Change Manager"
                              >
                                <UserCog className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Reassign Manager Modal ───────────────────────────────────────────────────

function ReassignModal({
  target,
  allUsers,
  onClose,
}: {
  target: OrgNode;
  allUsers: OrgNode[];
  onClose: () => void;
}) {
  const reassign = useReassignManager();
  const [selectedManager, setSelectedManager] = useState<string>(target.manager_id ?? '');
  const isTargetUzef = isUzefDurrani(target);

  const managerOptions = useMemo(() => {
    const opts = [{ value: '', label: '— No Manager (Top / Root) —' }];
    const uzef = allUsers.find(isUzefDurrani);
    if (uzef && uzef.id !== target.id) {
      opts.push({
        value: uzef.id,
        label: `👑 ${uzef.display_name} (Executive Leadership / Super Admin)`,
      });
    }
    for (const u of allUsers) {
      if (u.id === target.id) continue;
      if (isUzefDurrani(u)) continue;
      const deptConf = getDeptConfig(u.department);
      opts.push({
        value: u.id,
        label: `${u.display_name} (${ROLE_LABELS[u.role] ?? u.role} · ${deptConf.label})`,
      });
    }
    return opts;
  }, [allUsers, target.id]);

  const handleSave = () => {
    if (isTargetUzef) return;
    reassign.mutate(
      { userId: target.id, managerId: selectedManager || null },
      {
        onSuccess: () => {
          toast.success(`Manager updated for ${target.display_name}.`);
          onClose();
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to reassign manager.')),
      }
    );
  };

  const targetDept = getDeptConfig(target.department);

  return (
    <Modal
      open
      onClose={onClose}
      title="Change Reporting Manager"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {isTargetUzef ? 'Close' : 'Cancel'}
          </Button>
          {!isTargetUzef && (
            <Button onClick={handleSave} loading={reassign.isPending}>
              Save Reporting Line
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {/* Person Info Card */}
        <div className="flex items-center gap-3 rounded-xl border border-outline bg-surface-container-low/60 p-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-xs"
            style={{ backgroundColor: ROLE_COLORS[target.role] ?? '#6b7280' }}
          >
            {target.display_name
              .split(' ')
              .map((w) => w[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()}
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-bold text-on-surface truncate">{target.display_name}</h4>
            <p className="text-xs text-on-surface-variant">
              {ROLE_LABELS[target.role] ?? target.role} · {targetDept.label}
            </p>
            {target.position && (
              <span className="mt-1 inline-block text-[11px] font-medium text-primary">
                Position: {target.position}
              </span>
            )}
          </div>
        </div>

        {isTargetUzef ? (
          <div className="rounded-xl border border-violet-400/50 bg-violet-500/10 p-4 text-center">
            <Crown className="mx-auto h-8 w-8 text-violet-600 mb-2" />
            <h4 className="text-sm font-bold text-on-surface">Executive Leadership</h4>
            <p className="mt-1 text-xs text-on-surface-variant">
              Uzef Durrani is the Organization Super Administrator and Executive Leadership. They are at the summit of the hierarchy and do not report to anyone.
            </p>
          </div>
        ) : (
          <>
            <Select
              label="Direct Reporting Manager"
              name="new-manager"
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
              options={managerOptions}
            />

            <p className="text-[11px] text-on-surface-variant leading-relaxed">
              Updating the reporting line moves this team member and their direct reports under the designated manager in
              both the hierarchy tree and HR payroll/governance workflows.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}
